import React from 'react';
import {ListItem, ListItemText, Stack, Typography, useTheme} from '@mui/material';

import {TagChip} from 'presentation/components';
import {Drawer} from 'presentation/components/Drawer';
import Icon from 'presentation/icons';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatPriceBill, formatReferencia} from 'presentation/utils';

import {ChargeDetailsAccordion} from './styles';
import type {HistoryDetailsDrawerProps} from './types';

const {invoiceDetailsPage} = translations['pt-br'].privateFlow.myFinancial;
const {
	invoiceReference,
	dueDate,
	payment,
	invoiceValue,
	paymentType,
	cardBrand,
	paymentTypeCreditCard,
	paymentTypeDebitCard,
	paymentTypeGooglePay,
	paymentTypeApplePay,
	paymentTypePix,
	paymentTypeAutomaticPix,
	chargeDetails,
	chargeDetailsAccordion,
} = translations['pt-br'].privateFlow.myFinancial.paymentsHistory.detailsDrawer;

const PAYMENT_TYPE_LABEL_MAP: Record<string, string> = {
	CREDIT_CARD: paymentTypeCreditCard,
	C2P_CREDIT: paymentTypeCreditCard,
	DEBIT_CARD: paymentTypeDebitCard,
	C2P_DEBIT: paymentTypeDebitCard,
	GOOGLE_PAY: paymentTypeGooglePay,
	APPLE_PAY: paymentTypeApplePay,
	PIX: paymentTypePix,
	AUTOMATIC_PIX: paymentTypeAutomaticPix,
};

const CARD_PAYMENT_TYPES = ['CREDIT_CARD', 'DEBIT_CARD', 'C2P_CREDIT', 'C2P_DEBIT'];

function formatCardBrand(brand: string): string {
	if (!brand) return brand;
	return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
}

type IconName =
	| 'event-filled'
	| 'calendar-clock'
	| 'receipt-long-filled'
	| 'calendar-checked'
	| 'account-balance-default'
	| 'credit-card-default';

function InfoItem({
	iconName,
	primary,
	secondary,
}: {
	iconName: IconName;
	primary: string;
	secondary: string;
}) {
	const {palette} = useTheme();
	return (
		<ListItem disablePadding sx={{gap: 1, alignItems: 'flex-start'}}>
			<Stack padding={0.5}>
				<Icon name={iconName} color={palette.primary.main} size={20} />
			</Stack>
			<ListItemText
				sx={{margin: 0}}
				primary={
					<Typography variant="body2" color={palette.text.secondary} fontWeight={400}>
						{primary}
					</Typography>
				}
				secondary={
					<Typography variant="body1" fontWeight={400}>
						{secondary}
					</Typography>
				}
			/>
		</ListItem>
	);
}

function HistoryDetailsDrawer({open, onClose, row}: HistoryDetailsDrawerProps) {
	const {isMobile} = useMediaQueries();

	const paymentTypeLabel = row?.dsTipoPagamento
		? PAYMENT_TYPE_LABEL_MAP[row.dsTipoPagamento] ?? row.dsTipoPagamento
		: null;
	const shouldShowCardBrand =
		!!row?.dsCartaoBandeira &&
		!!row?.dsTipoPagamento &&
		CARD_PAYMENT_TYPES.includes(row.dsTipoPagamento);

	const monthlyFeeValue = row?.vlSemTaxa ?? row?.valor ?? 0;
	const interestValue = row?.vlTaxa ?? 0;
	const hasInterest = interestValue > 0;

	return (
		<Drawer.Root
			open={open}
			onClose={onClose}
			onOpen={() => {}}
			width="639px"
			customMaxHeight="100%"
			pullerPosition="absolute">
			<Drawer.Header containerPadding="16px" title={invoiceDetailsPage} onClose={onClose} />
			<Drawer.Content>
				{row ? (
					<Stack gap={2}>
						<Stack width="fit-content">
							<TagChip label="Pago" color="success" />
						</Stack>
						<Stack mt={2} gap={2}>
							{isMobile ? (
								<Stack gap={2}>
									<InfoItem
										iconName="event-filled"
										primary={invoiceReference}
										secondary={formatReferencia(row.referencia)}
									/>
									<InfoItem
										iconName="calendar-clock"
										primary={dueDate}
										secondary={row.vencimento}
									/>
									<InfoItem
										iconName="receipt-long-filled"
										primary={invoiceValue}
										secondary={formatPriceBill(row.valor)}
									/>
									<InfoItem
										iconName="calendar-checked"
										primary={payment}
										secondary={row.pagamento}
									/>
									{paymentTypeLabel ? (
										<InfoItem
											iconName="account-balance-default"
											primary={paymentType}
											secondary={paymentTypeLabel}
										/>
									) : null}
									{shouldShowCardBrand ? (
										<InfoItem
											iconName="credit-card-default"
											primary={cardBrand}
											secondary={formatCardBrand(row.dsCartaoBandeira as string)}
										/>
									) : null}
								</Stack>
							) : (
								<Stack direction="row" gap={2}>
									<Stack flex={1} gap={2}>
										<InfoItem
											iconName="event-filled"
											primary={invoiceReference}
										secondary={formatReferencia(row.referencia)}
										/>
										<InfoItem
											iconName="receipt-long-filled"
											primary={invoiceValue}
											secondary={formatPriceBill(row.valor)}
										/>
										{paymentTypeLabel ? (
											<InfoItem
												iconName="account-balance-default"
												primary={paymentType}
												secondary={paymentTypeLabel}
											/>
										) : null}
									</Stack>
									<Stack flex={1} gap={2}>
										<InfoItem
											iconName="calendar-clock"
											primary={dueDate}
											secondary={row.vencimento}
										/>
										<InfoItem
											iconName="calendar-checked"
											primary={payment}
											secondary={row.pagamento}
										/>
										{shouldShowCardBrand ? (
											<InfoItem
												iconName="credit-card-default"
												primary={cardBrand}
												secondary={formatCardBrand(row.dsCartaoBandeira as string)}
											/>
										) : null}
									</Stack>
								</Stack>
							)}
						</Stack>
						<ChargeDetailsAccordion title={chargeDetails}>
							<Stack gap={1.5}>
								<Stack
									direction="row"
									justifyContent="space-between"
									alignItems="center">
									<Typography variant="body1">
										{chargeDetailsAccordion.monthlyFee}
									</Typography>
									<Typography variant="body1">
										{formatPriceBill(monthlyFeeValue)}
									</Typography>
								</Stack>
								<Stack
									direction="row"
									justifyContent="space-between"
									alignItems="center">
									<Typography variant="body1">
										{chargeDetailsAccordion.interestAndFine}
									</Typography>
									<Typography
										variant="body1"
										color={hasInterest ? 'error' : undefined}>
										{hasInterest ? '+ ' : ''}
										{formatPriceBill(interestValue)}
									</Typography>
								</Stack>
								<Stack
									direction="row"
									justifyContent="space-between"
									alignItems="center">
									<Typography variant="body1">
										{chargeDetailsAccordion.totalToPayment}
									</Typography>
									<Typography variant="h6">{formatPriceBill(row.valor)}</Typography>
								</Stack>
							</Stack>
						</ChargeDetailsAccordion>
					</Stack>
				) : null}
			</Drawer.Content>
		</Drawer.Root>
	);
}

export {HistoryDetailsDrawer};
